import * as UTILS from './three-utils';
import * as FRAGS from 'bim-fragment';
import JSZip from 'jszip';
import { FragmentDataExporter } from './fragment-data-exporter';

export class FragmentExporter {
  constructor() {
    this.keys = ["lines", "holes", "areas", "items"];
    this.dataExporter = new FragmentDataExporter(this.keys);
    this.link = document.createElement('a');
    this.link.style.display = 'none';
    document.body.appendChild(this.link);
  }

  async export(props) {
    const exportData = this.dataExporter.export(props);
    const layers = window.__sceneGraph.layers;
    const fragments = [];
    this.getAllLayersFragments(layers, exportData, fragments);
    await this.save(fragments, exportData);
  }

  getAllLayersFragments(layers, exportData, fragments) {
    for (const layerID in layers) {
      const layer = layers[layerID];
      for (const key of this.keys) {
        const frags = this.getLayerFragments(key, layer, exportData);
        fragments.push(...frags);
      }
    }
  }

  getLayerFragments(key, layer, exportData) {
    const fragments = [];
    const geomsByCateg = {};
    this.processAllGroups(layer, key, geomsByCateg, exportData);
    this.createAllFragmentsByCategory(geomsByCateg, fragments, exportData);
    return fragments;
  }

  createAllFragmentsByCategory(geomsByCateg, fragments, exportData) {
    for (const category in geomsByCateg) {
      const geometriesByMaterial = geomsByCateg[category];
      const data = this.getGeometryData(geometriesByMaterial);
      this.getGeometriesByMaterial(geometriesByMaterial, data);
      if (data.geometries.length) {
        this.generateFragments(data, fragments, exportData);
      }
    }
  }

  generateFragments(data, fragments, exportData) {
    const merged = FRAGS.GeometryUtils.merge(data.geometries);
    merged.setAttribute('blockID', new THREE.BufferAttribute(data.buffer, 1));
    const ids = Array.from(data.visitedItems.keys());
    const mergedFragment = new FRAGS.Fragment(merged, data.materials, 1);
    mergedFragment.setInstance(0, { ids, transform: new THREE.Matrix4() });
    fragments.push(mergedFragment);
    for (const itemID of data.itemIDs) {
      exportData.fragmentMap[itemID] = mergedFragment.id;
    }
  }

  getGeometriesByMaterial(geometriesByMaterial, data) {
    for (const materialID in geometriesByMaterial) {
      if (materialID === 'size') continue;
      const { material, geoms } = geometriesByMaterial[materialID];
      data.materials.push(material);
      const geometryList = [];
      data.geometries.push(geometryList);
      this.saveGeometryByMaterial(geoms, data, geometryList);
    }
  }

  saveGeometryByMaterial(geoms, data, geometryList) {
    for (const itemID in geoms) {
      data.itemIDs.push(itemID);
      const geometries = geoms[itemID];
      this.mergeGeometries(geometries, geoms, itemID);
      const geometry = geoms[itemID];
      geometryList.push(geometry);
      this.addGeometryToBlockIDBuffer(geometry, data, itemID);
    }
  }

  mergeGeometries(geometries, geoms, itemID) {
    if (geometries.length > 1) {
      // Merge geometries that are in the same object with the same material
      geoms[itemID] = [UTILS.mergeBufferGeometries(geometries, false)][0];
    } else {
      geoms[itemID] = geoms[itemID][0];
    }
  }

  addGeometryToBlockIDBuffer(geometry, data, itemID) {
    const count = geometry.attributes.position.count;
    if (!data.visitedItems.has(itemID)) {
      data.visitedItems.set(itemID, data.blockID++);
    }

    const id = data.visitedItems.get(itemID);
    data.buffer.fill(id, data.offset, data.offset + count);
    data.offset += count;
  }

  getGeometryData(geometriesByMaterial) {
    return {
      buffer: new Uint16Array(geometriesByMaterial.size),
      materials: [],
      geometries: [],
      offset: 0,
      visitedItems: new Map(),
      blockID: 0,
      itemIDs: [],
    };
  }

  processAllGroups(layer, key, geomsByCateg, exportData) {
    for (const id in layer[key]) {
      const geomsByMat = this.getGeomsByMaterial(geomsByCateg, exportData, id);
      const item = layer[key][id];
      const scene = this.getScene(item);
      for (const group of item.children) {
        this.processGroup(group, geomsByMat, id, scene);
      }
    }
  }

  processGroup(group, geomsByMat, id, scene) {
    UTILS.addHelperFunctions(group);
    if (group.userData.exportableMeshes) {
      this.flatten(group);
      for (const mesh of group.userData.exportableMeshes) {
        this.processItemGeometry(mesh, geomsByMat, id, scene);
      }
    }
  }

  flatten(group) {
    if (group.userData.flatten) {
      const object = UTILS.flatten(group.userData.exportableMeshes[0]);
      group.userData.exportableMeshes = [...object.children];
    }
  }

  getScene(item) {
    const scene = item.parent.parent;
    UTILS.addHelperFunctions(Object.getPrototypeOf(scene));
    return scene;
  }

  getGeomsByMaterial(geometriesByCategory, exportData, itemID) {
    const category = exportData.modelTypes[itemID];
    if (!geometriesByCategory[category]) {
      geometriesByCategory[category] = { size: 0 };
    }
    return geometriesByCategory[category];
  }

  processItemGeometry(child, geomsByMat, itemID, scene) {
    const scaleMatrix = UTILS.getScaleMatrix(0.01);
    UTILS.addHelperFunctions(child);
    let bufferGeometry = this.getBufferGeometry(scene, child, scaleMatrix);
    if (bufferGeometry.attributes.position) {
      this.getGeomsByColor(geomsByMat, child.material, itemID, bufferGeometry);
      geomsByMat.size += bufferGeometry.attributes.position.count;
    }
  }

  getGeomsByColor(geomsByMat, mat, itemID, bufferGeometry) {
    const {color, opacity, transparent} = mat;
    const colorID = this.getColorID(color, opacity);
    this.initGeoms(geomsByMat, colorID, color, opacity, transparent, itemID);
    const geometries = geomsByMat[colorID].geoms[itemID];
    geometries.push(bufferGeometry);
  }

  initGeoms(geomsByMat, colorID, color, opacity, transparent, itemID) {
    if (!geomsByMat[colorID]) {
      geomsByMat[colorID] = {
        material: new THREE.MeshLambertMaterial({ color, opacity, transparent }),
        geoms: {},
      };
    }
    if (!geomsByMat[colorID].geoms[itemID]) {
      geomsByMat[colorID].geoms[itemID] = [];
    }
  }

  getColorID(color, opacity) {
    const { r, g, b } = color;
    const colorID = `${r}-${g}-${b}-${opacity}`;
    return colorID;
  }

  getBufferGeometry(scene, child, scaleMatrix) {
    scene.attach(child);
    child.applyMatrix(scaleMatrix);
    child.updateMatrix();
    const bufferGeometry = this.newBufferGeometry(child);
    if(bufferGeometry.attributes.position) {
      const count = bufferGeometry.attributes.position.count;
      bufferGeometry.groups = { materialIndex: 0, start: 0, count };
      this.setupGeometryAttrSetter(bufferGeometry);
      UTILS.makeGeometryIndexed(bufferGeometry);
    }
    return bufferGeometry;
  }

  setupGeometryAttrSetter(bufferGeometry) {
    if (!bufferGeometry.setAttribute) {
      const bufferGeomProto = Object.getPrototypeOf(bufferGeometry);
      bufferGeomProto.setAttribute = UTILS.setAttribute;
    }
  }

  newBufferGeometry(child) {
    let bufferGeometry = child.geometry._bufferGeometry || child.geometry;
    if (!bufferGeometry.attributes) {
      bufferGeometry = new THREE.BufferGeometry().fromGeometry(bufferGeometry);
    }

    delete bufferGeometry.attributes.uv;
    delete bufferGeometry.attributes.color;
    bufferGeometry.applyMatrix(child.matrix);
    return bufferGeometry;
  }

  async save(fragments, data) {
    const blob = await this.getFile(fragments, data);
    this.link.href = URL.createObjectURL(blob);
    this.link.download = 'test.zip';
    this.link.click();
    window.location.reload();
  }

  async getFile(fragments, data) {
    const zip = new JSZip();
    const files = [];
    await this.getAllFragmentFiles(fragments, files);
    this.getAllDataFiles(files, data);
    for (const file of files) {
      zip.file(file.name, file);
    }
    return zip.generateAsync({ type: 'blob' });
  }

  async getAllFragmentFiles(fragments, files) {
    for (const frag of fragments) {
      const file = await frag.export();
      files.push(file.geometry, file.data);
    }
  }

  getAllDataFiles(files, data) {
    files.push(new File([JSON.stringify(data.properties)], 'properties.json'));
    files.push(new File([JSON.stringify(data.levelsRelationships)], 'levels-relationship.json'));
    files.push(new File([JSON.stringify(data.modelTypes)], 'model-types.json'));
    files.push(new File([JSON.stringify(data.allTypes)], 'all-types.json'));
    files.push(new File([JSON.stringify(data.levelsProperties)], 'levels-properties.json'));
    files.push(new File([JSON.stringify(data.coordinationMatrix)], 'coordination-matrix.json'));
    files.push(new File([JSON.stringify(data.fragmentMap)], 'express-fragment-map.json'));
  }
}

